import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { DashboardLayout } from './Dashboard';
import {
  ChartLine, Crown, CurrencyEur, Users, Warning, Lightning, ArrowsClockwise, TrendUp, Envelope, CursorClick,
} from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const FEATURE_LABELS = {
  discovery: 'Discovery',
  auto_build: 'Auto-Build',
  product_generator: 'Product Generator',
  winner_score: 'Winner Score™',
};

const Stat = ({ label, value, sub, color = '#A855F7', icon: Icon }) => (
  <div className="p-5 bg-[#121212] border border-white/10 rounded-2xl">
    <div className="flex items-center gap-2 mb-2">
      {Icon && (
        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${color}22` }}>
          <Icon size={14} weight="bold" style={{ color }} />
        </div>
      )}
      <span className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-semibold">{label}</span>
    </div>
    <div className="text-3xl font-[Outfit] font-bold tracking-tight">{value}</div>
    {sub && <div className="text-xs text-white/50 mt-1">{sub}</div>}
  </div>
);

const FounderUsage = ({ user }) => {
  const { t, i18n } = useTranslation();
  const [data, setData] = useState(null);
  const [engagement, setEngagement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const [usage, eng] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/founder/usage`),
        axios.get(`${BACKEND_URL}/api/founder/email-engagement?days=30`).catch(() => ({ data: null })),
      ]);
      setData(usage.data);
      setEngagement(eng.data);
    } catch (err) {
      if (err?.response?.status === 403) {
        toast.error(t('fu.forbidden'));
      } else {
        toast.error(t('fu.load_fail'));
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [t]);

  const sendDigestNow = useCallback(async () => {
    if (!window.confirm(t('fu.confirm_digest'))) return;
    try {
      const res = await axios.post(`${BACKEND_URL}/api/founder/insights/send-now`);
      toast.success(res.data?.note || t('fu.digest_sent'));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('fu.send_fail'));
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const dateLocale = i18n.language === 'pt' ? 'pt-PT' : 'en-US';

  return (
    <DashboardLayout user={user} activePage="/founder/usage">
      <div className="p-6" data-testid="founder-usage-page">
        <div className="flex items-start justify-between gap-3 mb-8 flex-wrap">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#F59E0B]/15 border border-[#F59E0B]/30 rounded-full mb-3">
              <Crown size={14} weight="fill" className="text-[#F59E0B]" />
              <span className="text-[11px] tracking-[0.2em] uppercase text-[#F59E0B] font-semibold">{t('fu.badge')}</span>
            </div>
            <h1 className="text-4xl font-[Outfit] font-bold tracking-tight mb-2">{t('fu.title')}</h1>
            <p className="text-white/60 max-w-2xl">
              {t('fu.subtitle')}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={load}
              disabled={refreshing}
              data-testid="founder-refresh-btn"
              className="inline-flex items-center gap-1.5 px-3 py-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg text-xs uppercase tracking-wider transition disabled:opacity-50"
            >
              <ArrowsClockwise size={13} weight="bold" className={refreshing ? 'animate-spin' : ''} />
              {t('fu.refresh')}
            </button>
            <button
              onClick={sendDigestNow}
              data-testid="founder-send-digest-btn"
              className="inline-flex items-center gap-1.5 px-3 py-2 bg-gradient-to-r from-[#F59E0B] to-[#FBBF24] text-black hover:opacity-90 rounded-lg text-xs uppercase tracking-wider font-semibold transition"
              title={t('fu.send_digest_tip')}
            >
              <Crown size={13} weight="fill" />
              {t('fu.send_digest')}
            </button>
          </div>
        </div>

        {loading && (
          <div className="p-10 bg-[#0A0A0A] border border-white/10 rounded-2xl text-center">
            <Lightning size={28} weight="fill" className="mx-auto mb-3 text-[#A855F7] animate-pulse" />
            <p className="text-white/60 text-sm">{t('fu.loading')}</p>
          </div>
        )}

        {!loading && data && (
          <div className="space-y-6">
            {/* Warning banner */}
            {data.totals?.warning && (
              <div className="p-4 bg-red-500/10 border border-red-500/40 rounded-xl flex items-start gap-3" data-testid="cost-warning-banner">
                <Warning size={20} weight="fill" className="text-red-400 flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-[Outfit] font-bold text-red-300">{t('fu.cost_alert_title')}</div>
                  <div className="text-sm text-white/80">{t('fu.cost_alert_body')}</div>
                </div>
              </div>
            )}

            {/* Top stats */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
              <Stat
                label={t('fu.stat_cost')}
                value={`€${data.totals?.total_cost_30d_eur ?? 0}`}
                sub={t('fu.stat_cost_sub', { amount: data.totals?.total_cost_month_eur ?? 0 })}
                color="#EF4444"
                icon={CurrencyEur}
              />
              <Stat
                label={t('fu.stat_users')}
                value={data.users?.total ?? 0}
                sub={`Free ${data.users?.free} · Pro ${data.users?.pro} · Agency ${data.users?.agency}`}
                color="#A855F7"
                icon={Users}
              />
              <Stat
                label={t('fu.stat_discovery')}
                value={data.by_feature?.discovery?.last_30d ?? 0}
                sub={`€${data.by_feature?.discovery?.cost_30d_eur ?? 0} (~€${data.by_feature?.discovery?.cost_per_call_eur}/call)`}
                color="#06B6D4"
                icon={ChartLine}
              />
              <Stat
                label={t('fu.stat_autobuilds')}
                value={data.by_feature?.auto_build?.last_30d ?? 0}
                sub={`€${data.by_feature?.auto_build?.cost_30d_eur ?? 0} (~€${data.by_feature?.auto_build?.cost_per_call_eur}/call)`}
                color="#10B981"
                icon={Lightning}
              />
            </div>

            {/* By feature breakdown */}
            <div className="p-5 bg-[#121212] border border-white/10 rounded-2xl">
              <h3 className="font-[Outfit] font-bold mb-4">{t('fu.usage_by_feature')}</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-[10px] uppercase tracking-wider text-white/40 text-left">
                      <th className="pb-2 font-semibold">{t('fu.th_feature')}</th>
                      <th className="pb-2 font-semibold text-right">{t('fu.th_today')}</th>
                      <th className="pb-2 font-semibold text-right">{t('fu.th_month')}</th>
                      <th className="pb-2 font-semibold text-right">{t('fu.th_30d')}</th>
                      <th className="pb-2 font-semibold text-right">{t('fu.th_total')}</th>
                      <th className="pb-2 font-semibold text-right">{t('fu.th_cost30d')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(data.by_feature || {}).map(([k, v]) => (
                      <tr key={k} className="border-t border-white/5" data-testid={`feature-row-${k}`}>
                        <td className="py-2.5 font-semibold">{FEATURE_LABELS[k] || k}</td>
                        <td className="py-2.5 text-right text-white/80">{v.today}</td>
                        <td className="py-2.5 text-right text-white/80">{v.this_month}</td>
                        <td className="py-2.5 text-right text-white/80">{v.last_30d}</td>
                        <td className="py-2.5 text-right text-white/60">{v.all_time}</td>
                        <td className="py-2.5 text-right font-semibold text-[#F59E0B]">€{v.cost_30d_eur}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Top users */}
            <div className="p-5 bg-[#121212] border border-white/10 rounded-2xl">
              <div className="flex items-center gap-2 mb-4">
                <TrendUp size={16} weight="bold" className="text-[#F59E0B]" />
                <h3 className="font-[Outfit] font-bold">{t('fu.top_users')}</h3>
              </div>
              {(data.top_users || []).length === 0 ? (
                <p className="text-sm text-white/50">{t('fu.no_usage')}</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-[10px] uppercase tracking-wider text-white/40 text-left">
                        <th className="pb-2 font-semibold">#</th>
                        <th className="pb-2 font-semibold">Email</th>
                        <th className="pb-2 font-semibold">{t('fu.th_plan')}</th>
                        <th className="pb-2 font-semibold text-right">{t('fu.th_calls')}</th>
                        <th className="pb-2 font-semibold text-right">{t('fu.th_cost')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.top_users.map((u, i) => {
                        const totalCalls = Object.values(u.by_feature || {}).reduce((a, b) => a + b, 0);
                        const highCost = u.total_cost_30d_eur > 5;
                        return (
                          <tr key={u.user_id} className="border-t border-white/5" data-testid={`top-user-${i}`}>
                            <td className="py-2.5 text-white/40">{i + 1}</td>
                            <td className="py-2.5 truncate max-w-[200px]" title={u.email}>{u.email || '—'}</td>
                            <td className="py-2.5">
                              <span className={`px-2 py-0.5 rounded text-[10px] uppercase tracking-wider font-semibold ${
                                u.plan === 'agency' ? 'bg-[#F59E0B]/15 text-[#F59E0B]' :
                                u.plan?.startsWith('pro') ? 'bg-[#10B981]/15 text-[#10B981]' :
                                'bg-white/10 text-white/60'
                              }`}>
                                {u.plan || 'free'}
                              </span>
                            </td>
                            <td className="py-2.5 text-right text-white/80">{totalCalls}</td>
                            <td className={`py-2.5 text-right font-semibold ${highCost ? 'text-red-400' : 'text-[#F59E0B]'}`}>€{u.total_cost_30d_eur}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="text-xs text-white/40">
              {t('fu.snapshot', { date: new Date(data.as_of).toLocaleString(dateLocale) })}
            </div>

            {engagement && (
              <div className="space-y-4" data-testid="email-engagement-section">
                <div className="flex items-center gap-3 mt-8 pt-6 border-t border-white/5">
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-[#A855F7]/20">
                    <Envelope size={16} weight="bold" className="text-[#A855F7]" />
                  </div>
                  <div>
                    <h2 className="text-xl font-[Outfit] font-bold">{t('fu.email_engagement')}</h2>
                    <p className="text-xs text-white/50">{t('fu.engagement_sub', { days: engagement.window_days })}</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <Stat label={t('fu.total_clicks')} value={engagement.total_clicks} color="#A855F7" icon={CursorClick} />
                  <Stat label={t('fu.unique_users')} value={engagement.total_unique_users} color="#2563EB" icon={Users} />
                  <Stat label={t('fu.active_campaigns')} value={(engagement.campaigns || []).length} color="#10B981" icon={Envelope} />
                </div>

                {Array.isArray(engagement.campaigns) && engagement.campaigns.length > 0 ? (
                  <div className="p-5 bg-[#121212] border border-white/10 rounded-2xl">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-white/50 mb-3 font-semibold">{t('fu.by_campaign')}</div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-left text-[10px] uppercase tracking-wider text-white/40 border-b border-white/10">
                            <th className="pb-2">{t('fu.th_campaign')}</th>
                            <th className="pb-2 text-right">{t('fu.th_clicks')}</th>
                            <th className="pb-2 text-right">{t('fu.th_unique')}</th>
                            <th className="pb-2 text-right">{t('fu.th_last_click')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {engagement.campaigns.map((c) => (
                            <tr key={c.campaign} className="border-b border-white/5 last:border-0">
                              <td className="py-2.5">
                                <span className="font-mono text-xs text-white/90">{c.campaign}</span>
                              </td>
                              <td className="py-2.5 text-right font-bold text-[#A855F7]">{c.clicks}</td>
                              <td className="py-2.5 text-right text-white/70">{c.unique_users}</td>
                              <td className="py-2.5 text-right text-xs text-white/50">
                                {c.last_clicked_at ? new Date(c.last_clicked_at).toLocaleDateString(dateLocale) : '—'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : (
                  <div className="p-6 bg-[#0A0A0A] border border-dashed border-white/15 rounded-2xl text-center">
                    <Envelope size={28} weight="bold" className="text-white/30 mx-auto mb-2" />
                    <p className="text-sm text-white/55">{t('fu.no_clicks')}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default FounderUsage;
